import React, { useContext, useEffect } from 'react';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { PaginasOutrosRegimes } from '../../../../../../../../../interfaces/IDadosOutrosRegimes';
import ErrorMessage from '../../../../../../../../../components/errorMessage/errorMessage';
import styles from './receitaExportacoes.module.scss';
import ComponenteNCM from '../../../componenteNCM/componenteNCM';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import { useQuery } from '@tanstack/react-query';
import { ETipoOperacao } from '../../../../../../../../../enums/ETipoOperacao';
import {
  IReceitaExportacoes,
  IReceitaExportacoesNCM,
  IReceitaExportacoesVinculadaNCM,
} from '../../../../../../../../../interfaces/IReceitaExportacoes';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IReceitaExportacoesProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  setBaseDeCalculo: React.Dispatch<
    // eslint-disable-next-line no-undef
    React.SetStateAction<PaginasOutrosRegimes>
  >;
}

const ReceitaExportacoes = ({ isEdicao, setIsEdicao, setBaseDeCalculo }: IReceitaExportacoesProps) => {
  const [isEdicaoNcm, setIsEdicaoNcm] = React.useState<boolean>(false);
  const [isLoadingPost, setIsLoadingPost] = React.useState<boolean>(false);

  const [listaAgregada, setListaAgregada] = React.useState<
    (IReceitaExportacoesNCM | IReceitaExportacoesVinculadaNCM)[]
  >([]);

  const { postReceitaExportacoes, deleteReceitaExportacoes, getListaNCMsAdicionadoReceitaExportacoes } =
    useBackEndRequests();
  const context = useContext(DiefCfemContext);

  const mostrarNotificacao = useNotificacao();

  const {
    data: listaNCMData,
    isLoading: isListaNCMLoading,
    error: listaNCMError,
    refetch: refetchListaNCM,
  } = useQuery<IReceitaExportacoes, IErrorWithSentryId>({
    queryKey: ['getListaNCMsAdicionadoReceitaExportacoes', context?.idReceitaExportacoes],
    queryFn: () => getListaNCMsAdicionadoReceitaExportacoes(context?.idReceitaExportacoes ?? -1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: context?.idReceitaExportacoes !== -1,
  });

  useEffect(() => {
    if (listaNCMData) {
      const lista = [];

      if (listaNCMData.exportacoes) {
        lista.push(...listaNCMData.exportacoes);
      }

      if (listaNCMData.exportacoesVinculada) {
        lista.push(...listaNCMData.exportacoesVinculada);
      }

      setListaAgregada(lista);
    }
  }, [listaNCMData]);

  const deleteReceita = async () => {
    await deleteReceitaExportacoes(context?.idReceitaExportacoes || -1)
      .then(() => {
        setIsEdicao(false);
        setBaseDeCalculo('lista');
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao cancelar Receita Calculada (-) Exportações, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  useEffect(() => {
    if (!isEdicao) {
      setIsLoadingPost(true);
      const postReceita = async () => {
        await postReceitaExportacoes(context?.idOperacaoEditar || '-1')
          .then((response) => {
            context?.setIdReceitaExportacoes(response.id);
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao iniciar Receita Exportacoes, tente novamente.',
              ...(err && { complementoSentryId: err }),
            });
            setBaseDeCalculo('lista');
          });
      };
      postReceita();
      setIsLoadingPost(false);
    }
  }, []);

  return (
    <div className={styles.container}>
      {listaNCMError ? (
        <ErrorMessage
          errorMessage={''}
          error={listaNCMError}
          onClickFunction={() => {
            setIsEdicao(false);
            setBaseDeCalculo('lista');
          }}
        />
      ) : (
        <>
          {isLoadingPost ? (
            <div className={styles.loadingContainer}>
              <BrLoading large />
            </div>
          ) : (
            <>
              <div className={styles.titleContainer}>
                <span className={styles.title}>Operação - Receita Calculada (-) Exportações</span>
              </div>
              <div className={styles.ncmContainer}>
                <ComponenteNCM
                  isEdicaoNcm={isEdicaoNcm}
                  setIsEdicaoNcm={setIsEdicaoNcm}
                  ncmListaData={listaAgregada}
                  isListaNCMLoading={isListaNCMLoading}
                  listaNCMError={listaNCMError}
                  refetchListaNCM={refetchListaNCM}
                  type={ETipoOperacao.RECEITA_CALCULADA_EXPORTACOES}
                  insideTitle
                />
              </div>
              <div className={styles.containerButton}>
                {!isEdicao && (
                  <BrButton secondary onClick={() => deleteReceita()}>
                    <span className={styles.button}>
                      <i className={`fas fa-trash`} /> Cancelar
                    </span>
                  </BrButton>
                )}
                <BrButton
                  onClick={() =>
                    listaNCMData?.exportacoes?.length === 0 && listaNCMData?.exportacoesVinculada?.length === 0
                      ? mostrarNotificacao({
                          tipo: 'danger',
                          conteudo: 'Insira pelo menos um NCM para continuar.',
                        })
                      : setBaseDeCalculo('lista')
                  }
                  primary
                >
                  <span className={styles.button}>
                    {isEdicao ? (
                      <>
                        <i className={`fas fa-save`} /> Salvar Base de Cálculo
                      </>
                    ) : (
                      <>
                        <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                      </>
                    )}
                  </span>
                </BrButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReceitaExportacoes;
